/**
 * Pure FSM for the architecture-map view (plans/architecture-map-c4.md).
 *
 * Dependency-free (no zustand/preact) so it compiles + runs standalone under the SE harness
 * (setpoint-evals/arch-map/SE-05). `archStore.ts` consumes `TRANSITIONS` + `nextPhase` — the store
 * owns I/O + persistence; this module owns the legal transition table (R-A1).
 */
export type ArchPhase = 'idle' | 'loading' | 'rendered' | 'error';

export interface ArchTransition {
  from: ArchPhase;
  to: ArchPhase;
  trigger: string;
}

/**
 * Canonical transition table (R-A1). `requestGraph` is the single intent behind the initial load,
 * a node drill, and a breadcrumb jump (all `* → loading`). `selectNode`/`closeNode`/`setSearchQuery`/
 * `runSearch`/`restoreFromHash` are data-only self-loops in `rendered` (the node panel / list filter /
 * cross-corpus search palette / hash-popstate settle mutate without leaving the rendered phase).
 *
 * NOTE (R-A1 honesty): this is 10 rows (mobile-redesign Phase 2, arch-map-mobile-redesign.md §
 * "Navigation & state model"). 8 existing + 2 new:
 *   - `runSearch` — the cross-corpus SE-name search palette (Phase 2a). Commits/clears the query
 *     (`runSearch(null)` closes, `runSearch('')` opens empty, `runSearch(q)` commits text) — one
 *     named action covers open/type/close, mirroring how `requestGraph` covers initial/drill/crumb.
 *   - `restoreFromHash` — the popstate / cold-load re-derive (Phase 2b, the `#arch/<level>/<id>`
 *     deep-link). When the parsed hash target DIFFERS from the current position, `restoreFromHash`
 *     delegates to `requestGraph` (already covered by the `*→loading` rows above — no double-count).
 *     This row covers the OTHER branch: the hash matches where we already are (a malformed/missing
 *     hash, or popping back from CodeViewer's `#file:` overlay to the still-current `#arch:` entry) —
 *     a real, nameable "settled, nothing to refetch" event (R-A2), not a silent no-op.
 * SE-05 asserts the exact count + that every (from,trigger) is deterministic and resolves.
 */
export const TRANSITIONS: ArchTransition[] = [
  { from: 'idle', to: 'loading', trigger: 'requestGraph' },
  { from: 'rendered', to: 'loading', trigger: 'requestGraph' },
  { from: 'error', to: 'loading', trigger: 'requestGraph' },
  { from: 'loading', to: 'rendered', trigger: 'graphLoaded' },
  { from: 'loading', to: 'error', trigger: 'graphFailed' },
  { from: 'rendered', to: 'rendered', trigger: 'selectNode' },
  { from: 'rendered', to: 'rendered', trigger: 'closeNode' },
  { from: 'rendered', to: 'rendered', trigger: 'setSearchQuery' },
  { from: 'rendered', to: 'rendered', trigger: 'runSearch' },
  { from: 'rendered', to: 'rendered', trigger: 'restoreFromHash' },
];

/** Pure transition function: the next phase for (phase,trigger), or null if not allowed. */
export function nextPhase(phase: ArchPhase, trigger: string): ArchPhase | null {
  const t = TRANSITIONS.find((x) => x.from === phase && x.trigger === trigger);
  return t ? t.to : null;
}
